"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const OrderStatus = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const [statuses, setStatuses] = useState([]);
  const [newStatus, setNewStatus] = useState({ name: "" });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(
        `https://logicx.premierlam.com/api/pixel-get-pixel-order-status`,
        {
          headers: { authorization: localStorage.getItem("accessToken") },
        }
      );
      setStatuses(res.data.data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddStatus = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await axios.post(
        `https://logicx.premierlam.com/api/add-pixel-order-status`,
        { order_status: newStatus.name },
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        }
      );
      await fetchStatuses();
      setNewStatus({ name: "" });
      setIsAddDialogOpen(false);
      toast.success("Order status added successfully!");
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || "Failed to add order status");

    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const filteredStatus = statuses.filter((s) =>
    s.order_status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(statuses.map((v) => v.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <>
     <ToastContainer position="top-right" autoClose={3000} />
    <div className="min-h-screen bg-gray-50 p-2">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">
              Order Status
            </h1>
            <p className="text-gray-500">Manage status list for orders</p>
          </div>

          <button
            onClick={() => setIsAddDialogOpen(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Status
          </button>
        </div>

        {/* Table Container */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">

          {/* Search */}
          <div className="relative mb-6">
            <svg
              className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search order status"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none w-64"
            />
          </div>

          {/* TABLE */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="py-3 px-4 w-12 text-left">
                      <input
                        type="checkbox"
                        checked={selectedItems.length === statuses.length}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-orange-500"
                      />
                    </th>
                    <th className="py-3 px-4 text-sm font-medium text-gray-600 text-left">Order Status</th>
                    <th className="py-3 px-4 text-sm font-medium text-gray-600 text-left">Created On</th>
                    <th className="py-3 px-4 text-sm font-medium text-gray-600 text-left">Added By</th>
                    <th className="py-3 px-4 text-sm font-medium text-gray-600 text-left">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredStatus.map((s) => (
                    <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(s.id)}
                          onChange={() => handleSelectItem(s.id)}
                          className="rounded border-gray-300 text-orange-500"
                        />
                      </td>

                      <td className="py-3 px-4 text-sm text-gray-900">{s.order_status}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{formatDate(s.created_at)}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{s.added_by}</td>

                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <button
                            className="p-1 text-gray-400 hover:text-blue-600"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>

                          <button
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Add Status Modal */}
        {isAddDialogOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-full max-w-md relative">
              <button
                className="absolute right-3 top-2 text-gray-400 hover:text-gray-700"
                onClick={() => setIsAddDialogOpen(false)}
              >
                ✖
              </button>

              <h2 className="text-lg font-semibold mb-4">Add Order Status</h2>
              <form onSubmit={handleAddStatus} className="space-y-3">
                <input
                  type="text"
                  placeholder="Enter order status"
                  value={newStatus.name}
                  onChange={(e) => setNewStatus({ name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />

                <div className="flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => setIsAddDialogOpen(false)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg"
                  >
                    {isSubmitting ? "Adding..." : "Add Status"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </div>
    </>
  );
};

export default OrderStatus;
